import { Box, CardContent, Chip, Stack, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import {
  ExtractionResultItem,
  RagSourceReference,
} from '../../../../../api/document-rag/document-rag.api.types';
import { ExtractedResultsCardValues } from '../extraction-results-card/ExtractedResultsCardValues';

import classes from '../ExtractionResultCard.module.css';

export interface ExtractionResultsContentProps {
  /** Extraction result data from RAG API - can be single item or array for multiple values */
  extraction: ExtractionResultItem | ExtractionResultItem[];
  /** Callback when copy button is clicked */
  onCopy?: () => void;
  /** Callback when swap button is clicked - receives the currently selected extraction value(s) */
  onSwapValues?: (
    values: ExtractionResultItem | ExtractionResultItem[]
  ) => void;
  /** Callback when expand button is clicked to open full chat interface */
  onExpand?: () => void;
  /** Callback when a citation/source chip is clicked */
  onCitationClick?: (citation: RagSourceReference) => void;
  /** Optional: Field label to display in the popover */
  fieldLabel?: string;
  /** Callback when collapse button is clicked */
  onCollapse?: () => void;
  /** Whether to show the collapse button */
  showCollapseButton?: boolean;
  /** Whether the card is in a loading state */
  isLoading?: boolean;
  /** Optional className for styling */
  className?: string;
  /** Whether to show the header */
  showHeader?: boolean;
  /** Whether multi-select mode is enabled */
  isMultiSelect?: boolean;
  /** Current field value from the form - used to pre-select matching values in multi-select mode */
  currentFieldValue?: string | string[] | null;
}

export function ExtractionResultsContent({
  extraction,
  fieldLabel,
  onSwapValues,
  onCitationClick,
  isMultiSelect = false,
  currentFieldValue,
}: ExtractionResultsContentProps) {
  // Normalize to array - if single item, wrap in array
  const extractionResultsArray = Array.isArray(extraction)
    ? extraction
    : [extraction];

  // Deduplicate values based on id field to prevent duplicates in dropdown
  // This handles cases where values are manually added and might duplicate existing entries
  const extractionResults = extractionResultsArray.filter(
    (item, index, self) => {
      // If id exists, use it for deduplication
      if (item.id) {
        return index === self.findIndex((t) => t.id === item.id);
      }
      // For items without id, fall back to raw_value deduplication (backward compatibility)
      // Only compare against other items without id to avoid incorrect filtering
      if (item.raw_value === null || item.raw_value === undefined) {
        return true;
      }
      // For items with raw_value but no id, keep only the first occurrence
      // Compare only against items that also don't have an id
      return (
        index === self.findIndex((t) => !t.id && t.raw_value === item.raw_value)
      );
    }
  );

  // Calculate initial selected indices based on current field value
  const initialSelectedIndices = useMemo(() => {
    if (!isMultiSelect || !currentFieldValue) {
      return [];
    }

    // Normalize current field value to array of strings
    const currentValues = Array.isArray(currentFieldValue)
      ? currentFieldValue.map((v) => String(v))
      : [String(currentFieldValue)];

    // Find indices of extraction results that match current field values
    return extractionResults
      .map((item, index) => {
        const rawValueStr = item.raw_value?.toString();
        const formattedValueStr = item.formatted_value?.toString();

        // Check if either raw_value or formatted_value matches any current value
        const isMatch = currentValues.some(
          (currentVal) =>
            currentVal === rawValueStr || currentVal === formattedValueStr
        );

        return isMatch ? index : -1;
      })
      .filter((index) => index !== -1);
  }, [isMultiSelect, currentFieldValue, extractionResults]);

  // Track selected value index (default to first item) - for single select mode
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Track selected indices for multi-select mode
  const [selectedIndices, setSelectedIndices] = useState<number[]>([]);

  // Track whether initial selection has been applied to prevent re-applying on user deselection
  const [hasAppliedInitialSelection, setHasAppliedInitialSelection] =
    useState(false);

  // Apply initial selection only once when extraction results first load
  useEffect(() => {
    if (
      isMultiSelect &&
      !hasAppliedInitialSelection &&
      initialSelectedIndices.length > 0
    ) {
      setSelectedIndices(initialSelectedIndices);
      setHasAppliedInitialSelection(true);
    }
  }, [isMultiSelect, initialSelectedIndices, hasAppliedInitialSelection]);

  // Get the currently selected extraction result (for single select mode)
  // Fall back to first item if selectedIndex is out of bounds
  const selectedExtraction =
    extractionResults[selectedIndex] ?? extractionResults[0];

  // Early return if no extraction results available
  if (!selectedExtraction) {
    return null;
  }

  // Use summary from current extraction if available, otherwise fall back to snippet
  const summary = selectedExtraction.summary;
  const relevantPassage = summary || selectedExtraction.snippet || '';

  function handleExtractionSelect(index: number) {
    setSelectedIndex(index);
  }

  function handleMultiSelect(indices: number[]) {
    setSelectedIndices(indices);
  }

  return (
    <CardContent className={classes.cardContent}>
      <Stack spacing={0}>
        {/* Field Label and Value Section */}
        <Box className={classes.valueSection}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
          >
            <Stack spacing={1} sx={{ flex: 1, width: '100%' }}>
              <Box
                display="flex"
                alignItems="flex-start"
                justifyContent="space-between"
                sx={{ padding: '1rem 1rem 0 1rem' }}
              >
                <Typography className={classes.fieldLabel}>
                  {fieldLabel || selectedExtraction.field || '-'}
                </Typography>
              </Box>

              <Box className={classes.extractedValuesContainer}>
                <ExtractedResultsCardValues
                  value={selectedExtraction}
                  values={extractionResults}
                  onCopy={() => null}
                  onSwapValues={onSwapValues}
                  onSelect={handleExtractionSelect}
                  onMultiSelect={handleMultiSelect}
                  isLoading={false}
                  isMultiSelect={isMultiSelect}
                  selectedIndices={selectedIndices}
                />
              </Box>
            </Stack>
          </Box>
        </Box>

        {/* Relevant Passage */}
        {relevantPassage && (
          <Box className={classes.passageSection}>
            <Typography className={classes.passageText}>
              {relevantPassage}
            </Typography>
          </Box>
        )}

        {/* Source Document Tag */}
        {selectedExtraction.source && (
          <Box className={classes.tagsSection}>
            <Stack
              direction="row"
              spacing={0.5}
              flexWrap="nowrap"
              gap={0.5}
              sx={{ overflowX: 'scroll', paddingBottom: '1rem' }}
            >
              <Chip
                label={
                  (selectedExtraction.filename || selectedExtraction.source) +
                  (selectedExtraction.page
                    ? ` (Page ${selectedExtraction.page})`
                    : '')
                }
                className={classes.sourceTag}
                onClick={() => {
                  // Create a RagSourceReference from ExtractionResultItem for compatibility
                  // Include field name and extracted value for precise highlighting
                  onCitationClick?.({
                    documentId:
                      selectedExtraction.source ||
                      selectedExtraction.filename ||
                      '',
                    pageNumber: selectedExtraction.page,
                    snippet: selectedExtraction.snippet || '',
                    confidence: selectedExtraction.confidence_score,
                    metadata: {
                      fileName: selectedExtraction.filename,
                      source: selectedExtraction.source,
                      ...(selectedExtraction.page && {
                        page: selectedExtraction.page,
                      }),
                      // Include field name and extracted value for highlighting
                      field: selectedExtraction.field,
                      extractedValue:
                        selectedExtraction.formatted_value ??
                        selectedExtraction.raw_value,
                    },
                  });
                }}
              />
            </Stack>
          </Box>
        )}
      </Stack>
    </CardContent>
  );
}
